import jsPDF from 'jspdf';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { overlayInfo, resolveConstantStatics, THRESHOLD_FONTSIZE } from '../docOvelayTool';
import { buildReportBlob, fetchSourcePdf } from '../mergeSourcePdf';
import { RootState } from '../../../redux/root';
import { getSignature } from '../../../api/userAuth';
import useUserRole from '../../../hooks/useUserRole';
import Loader from '../../../atoms/Loader';

interface Props {
  data: any;
  onReady?: (uri: string) => void;
}

const PDFReportPreview = ({ data, onReady }: Props) => {
  const [base64, setBase64] = useState<string>('');
  const selectedForm = useSelector((s: RootState) => s.thresholdForms.selectedForm);
  const userId = useSelector((s: RootState) => s.userAuth.info.id);
  const [signature, setSignature] = useState<string>('');
  const { isInspector } = useUserRole();

  useEffect(() => {
    const loadSignature = async () => {
      const result = await getSignature(userId);
      setSignature(result);
    };
    loadSignature();
  }, []);

  useEffect(() => {
    if (!selectedForm || (isInspector && !signature)) return;

    const build = async () => {
      try {
        // The form's original PDF, when the backend kept one. Present => the
        // page is composited as real vector content instead of a 200-DPI PNG.
        const sourceBytes = await fetchSourcePdf(selectedForm.sourceFile);

        // eslint-disable-next-line new-cap
        const doc = new jsPDF();
        doc.setFontSize(THRESHOLD_FONTSIZE);
        // Skip the raster background only when there is something better to put
        // behind the text.
        // Static fields with a constant value (e.g. "Elite Permits Name") are
        // filled in first. In the builder there is no inspection to resolve
        // against, so without this they printed their '-test' placeholder.
        overlayInfo(
          doc,
          selectedForm.fileBase64,
          resolveConstantStatics(data),
          signature,
          !!sourceBytes,
        );

        // Build ONE blob URL and use it for both the iframe and the parent's
        // "open in new tab". jsPDF's output() is not reliably idempotent — a
        // second output() call can yield a corrupt PDF (fine in the embedded
        // viewer, crashes a full browser tab).
        const url = URL.createObjectURL(await buildReportBlob(doc, sourceBytes));
        setBase64(url);
        if (onReady) onReady(url);
      } catch (e) {
        // Never let a preview failure white-screen the builder.
        // eslint-disable-next-line no-console
        console.error('Failed to build report preview', e);
      }
    };
    build();
  }, [selectedForm, signature]);

  // The form's scanned page images live in the backend's media storage. If they
  // are missing (e.g. a database copied without its media folder) the API
  // returns file_base64: null and the report renders on blank pages.
  const missingPageImages = !!selectedForm && !(selectedForm.fileBase64 || []).length;

  return (
    <Viewport>
      {base64 ? (
        <Rendered>
          {missingPageImages && (
            <Notice>
              The scanned form image isn’t available for this form, so the report is drawn on blank pages. Re-upload
              the form’s PDF (Settings → Inspection Form Builder) to restore the background.
            </Notice>
          )}
          <Frame title="Report preview" src={base64} />
        </Rendered>
      ) : (
        <Loading>
          <Loader />
          <span>Generating preview…</span>
        </Loading>
      )}
    </Viewport>
  );
};

export default PDFReportPreview;

const Viewport = styled.div`
  width: 100%;
  height: 100%;
  min-height: 500px;
  display: flex;
`;

const Rendered = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Notice = styled.div`
  flex: 0 0 auto;
  margin-bottom: 10px;
  padding: 10px 12px;
  border: 1px solid var(--warning-100);
  border-radius: 8px;
  background: var(--warning-50);
  color: var(--neutral-700);
  font-size: 0.8125rem;
  line-height: 1.45;
`;

const Frame = styled.iframe`
  flex: 1;
  width: 100%;
  border: none;
  background: var(--neutral-0);
`;

const Loading = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--neutral-500);
  font-size: 0.875rem;
`;
